import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import moment from "moment";
import { auth, db } from "../lib/firebase";
import "../styles/GetUser.css";
import girlieIMG from "../images/girlie.jpeg";
import broIMG from "../images/bro.jpeg";
import loginIMG from "../images/page.webp";
import img1 from "../images/detail-man.jpg";
import img2 from "../images/hero-img-2.jpg";
import img3 from "../images/trade-girl.webp";
import img4 from "../images/detail.jpg";
import mamitoIMG from "../images/mamito.jpeg";
import useInView from "../lib/useInView";
import Loader from "../components/Loader";
import { sendBothEmails } from "../lib/AuthEmail";

const GetAuth = ({ setUser }) => {
  const BRAND_NAME = process.env.REACT_APP_BRAND_NAME;
  const REFERRAL_BONUS = parseFloat(
    process.env.REACT_APP_REFERRAL_BONUS || "0",
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [referrerName, setReferrerName] = useState("");

  const navigate = useNavigate();
  const { referrerId } = useParams();

  const [formType, setFormType] = useState(() =>
    referrerId ? "register" : "login",
  );

  const [boxImgRef, boxImgInView] = useInView({ threshold: 0.5 });
  const [box7Ref, box7InView] = useInView({ threshold: 0.7 });
  const [box8Ref, box8InView] = useInView({ threshold: 0.7 });
  const [box9Ref, box9InView] = useInView({ threshold: 0.7 });
  const [heroRef, heroInView] = useInView({ threshold: 0.5 });

  // * Fetch referrer's name using ID in URL
  useEffect(() => {
    const fetchReferrerName = async () => {
      if (!referrerId) return;
      try {
        const refDoc = await getDoc(doc(db, "users", referrerId));
        if (refDoc.exists()) {
          const refData = refDoc.data();
          if (refData.fullName) {
            setReferrerName(refData.fullName);
          }
        }
      } catch (error) {
        console.error("Error fetching referrer:", error);
      }
    };
    fetchReferrerName();
  }, [referrerId]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        setUser({ uid: user.uid, ...userDoc.data() });
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      setError("Please enter correct info to login to gain access");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate("/");
      }
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, [navigate]);

  if (!authChecked) {
    return <Loader />;
  }

  const handleRegister = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      const generateUniqueAccountNumber = async () => {
        let accountNumber;
        let isUnique = false;
        while (!isUnique) {
          accountNumber = Math.floor(
            100000000000 + Math.random() * 900000000000,
          ).toString();
          const usersSnapshot = await getDocs(collection(db, "users"));
          const existing = usersSnapshot.docs.map(
            (doc) => doc.data().accountNumber,
          );
          if (!existing.includes(accountNumber)) {
            isUnique = true;
          }
        }
        return accountNumber;
      };

      const generateCode = () =>
        Math.floor(100000 + Math.random() * 900000).toString();

      const accountNumber = await generateUniqueAccountNumber();
      const code = generateCode();

      await setDoc(doc(db, "users", user.uid), {
        fullName,
        address,
        gender,
        dob,
        email,
        group: "cancelled",
        role: "user",
        accountNumber,
        code,
        transactions: [], // initialize with empty transaction array
      });

      // Referral bonus logic
      if (referrerId) {
        const referralTransaction = {
          account: "Investments",
          amount: REFERRAL_BONUS,
          beneficiary: "ref",
          date: moment().format("MMMM D, YYYY"),
          time: moment().format("HH:mm:ss"),
          description: "A New Referral Bonus",
          type: "credit",
          stage: "complete",
        };

        try {
          await updateDoc(doc(db, "users", referrerId), {
            transactions: arrayUnion(referralTransaction),
          });
        } catch (error) {
          console.error("Error adding referral transaction:", error);
        }
      }

      setUser({
        uid: user.uid,
        fullName,
        address,
        gender,
        dob,
        email,
        group: "cancelled",
        role: "user",
        accountNumber,
        code,
      });

      console.log("🚀 Attempting to send registration emails...");
      try {
        await sendBothEmails({
          uid: user.uid,
          fullName,
          email,
          gender,
          dob,
          address,
          accountNumber,
          code,
          referrerName,
        });
      } catch (emailError) {
        console.error("❌ Email sending failed:", emailError);
      }
    } catch (error) {
      console.error("❌ Registration error:", error);
      if (error.code === "auth/email-already-in-use") {
        setError(
          "This email is already registered. Please use a different email or log in.",
        );
      } else {
        setError(
          "Error: Could not complete registration. Please check details and try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <div id="hero-section">
        <div className="hero-section">
          <div className="box">
            <div className="auth-containers">
              {formType === "login" && (
                <>
                  <h2 className="log">Login to Gain Access</h2>
                  <br />
                  <br />

                  <form onSubmit={handleLogin}>
                    {/* enter email address */}
                    <div>
                      <label className="log">Email:</label>
                      <input
                        type="email"
                        value={email}
                        className="input-field"
                        placeholder="eg. johndoe@email.com"
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>

                    {/* enter password */}
                    <div>
                      <label className="log pass">Password:</label>
                      <input
                        type="password"
                        className="input-field"
                        placeholder="********"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />

                    </div>

                    {/* message to handle errors */}
                    {error && <p style={{ color: "red" }}>{error}</p>}
                    <button
                      type="submit"
                      className="delete-button"
                      disabled={loading}
                    >
                      {loading ? "Logging in ..." : "Login"}
                    </button>
                  </form>

                  {/* open the registration fields */}
                  <span
                    onClick={() => setFormType("register")}
                    className="reg point"
                  >
                    Don't have an Account? Register Now
                  </span>
                </>
              )}

              {formType === "register" && (
                <>
                  <h2 className="log">Register an Account</h2>
                  <br />
                  {/* Referral Message */}
                  {referrerName && (
                    <div className="referral-message">
                      <p style={{ color: "green", fontWeight: "bold" }}>
                        You were referred by {referrerName}
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleRegister}>
                    <div className="form-covers">
                      {/* enter full name */}
                      <div>
                        <label className="log">Full Name:</label>
                        <input
                          type="text"
                          value={fullName}
                          placeholder="eg. John Doe"
                          className="input-field"
                          onChange={(e) => setFullName(e.target.value)}
                          required
                        />
                      </div>

                      {/* enter home address */}
                      <div>
                        <label className="log">Address:</label>
                        <input
                          type="text"
                          value={address}
                          className="input-field"
                          placeholder="eg. 123 Expressway, NY"
                          onChange={(e) => setAddress(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-covers">
                      {/* select your gender */}
                      <div>
                        <label className="log">Gender:</label>
                        <select
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                          required
                        >
                          <option value="" disabled hidden>
                            Select a Gender
                          </option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      </div>

                      {/* select account type */}
                      <div>
                        <label className="log">Date of Birth:</label>
                        <input
                          type="date"
                          value={dob}
                          className="input-field"
                          onChange={(e) => setDob(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-covers">
                      {/* enter email address */}
                      <div>
                        <label className="log">Email:</label>
                        <input
                          type="email"
                          value={email}
                          className="input-field"
                          placeholder="eg. johndoe@email.com"
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>

                      {/* enter password */}
                      <div>
                        <label className="log">Password:</label>
                        <input
                          type="password"
                          value={password}
                          className="input-field"
                          placeholder="********"
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    {/* handle error message */}
                    {error && <p style={{ color: "red" }}>{error}</p>}
                    <button
                      type="submit"
                      className="delete-button"
                      disabled={loading}
                    >
                      {loading ? "Registering ..." : "Register"}
                    </button>
                  </form>

                  <div
                    className="reg point"
                    onClick={() => setFormType("login")}
                  >
                    Already a User? Login to Continue
                  </div>
                </>
              )}

            </div>
          </div>

          <div className="box">
            <div className="auth-box-grid">
              <div className="auth-box">
                <img
                  src={img1}
                  alt="Box 1"
                  ref={heroRef}
                  className={`box-image animate__animated ${heroInView ? "custom-animation" : ""}`}
                />
              </div>
              <div className="auth-box">
                <img
                  src={img2}
                  ref={heroRef}
                  className={`box-image animate__animated ${heroInView ? "custom-animation" : ""}`}
                  alt="Box 2"
                />
              </div>
              <div className="auth-box">
                <img
                  src={img3}
                  ref={heroRef}
                  className={`box-image animate__animated ${heroInView ? "custom-animation" : ""}`}
                  alt="Box 3"
                />
              </div>
              <div className="auth-box">
                <img
                  src={img4}
                  alt="Box 4"
                  ref={heroRef}
                  className={`box-image animate__animated ${heroInView ? "custom-animation" : ""}`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* next content here */}
      <div className="main-box-cover">
        <h2 className="text-heading center">
          More Choices - <span> More Possibilities.</span>
        </h2>
        <div className="center">
          We offer our clients comprehensive investment solutions that are
          tailored to each particular client’s needs at a specific period of
          time. For 17 years, we have been providing secure and easy U.S.
          banking to over 400,000 who live, travel, shop and work in the U.S. We
          make cross-border banking easy and secure.
        </div>
      </div>

      <div className="last-section">
        <div className="box">
          <img
            ref={boxImgRef}
            src={loginIMG}
            alt={BRAND_NAME}
            className={`box-image animate__animated ${boxImgInView ? "animate__pulse" : ""}`}
          />
          <h2 className="text-heading center">
            Navigate complexity with a trusted capital markets services partner
            at your side
          </h2>
        </div>
        <div className="box">
          <div
            ref={box7Ref}
            className={`icon-title-text-container animate__animated ${box7InView ? "animate__zoomInRight" : ""}`}
          >
            <img
              src={mamitoIMG}
              className="boxed-icon"
              alt="WORKING AND INVESTING IN THE U.S."
            />
            <div className="title-text">
              <h3 className="box-title">WORKING AND INVESTING IN THE U.S.</h3>
              <div className="">
                Explore endless career and investment opportunities in the U.S.
                – from building a thriving career to making smart financial
                moves in one of the world's strongest economies.{" "}
              </div>
            </div>
          </div>

          <div
            ref={box8Ref}
            className={`icon-title-text-container animate__animated ${box8InView ? "animate__zoomInRight" : ""}`}
          >
            <img
              src={broIMG}
              className="boxed-icon"
              alt="RELOCATING AND SCHOOLING IN THE U.S."
            />
            <div className="title-text">
              <h3 className="box-title">
                RELOCATING AND SCHOOLING IN THE U.S.
              </h3>
              <div className="">
                Make your move to the U.S. seamless—discover the best schools,
                opportunities, and everything you need for a successful
                transition.
              </div>
            </div>
          </div>

          <div
            ref={box9Ref}
            className={`icon-title-text-container animate__animated ${box9InView ? "animate__zoomInRight" : ""}`}
          >
            <img
              src={girlieIMG}
              className="boxed-icon"
              alt="INTERNATIONAL BUSINESS WITH FOREIGNERS"
            />
            <div className="title-text">
              <h3 className="box-title">
                INTERNATIONAL BUSINESS WITH FOREIGNERS
              </h3>
              <div className="">
                Seamlessly conduct international transactions, manage foreign
                investments, and build strong financial partnerships with global
                banking solutions.{" "}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GetAuth;
